import React, { useState, useEffect } from 'react';
import QuestionRenderer from './QuestionRenderer';
import ProgressBar from './ProgressBar';
import { shuffleArray } from '../utils/scoring';

export default function Stage({ 
  stage, 
  questions, 
  onComplete, 
  totalStages 
}) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1800);
  const [shuffledOptionsMap, setShuffledOptionsMap] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setTimeLeft(1800);
    setIsSubmitting(false);
  }, [stage.stage]);
  
  useEffect(() => {
    const optionsMap = {};
    questions.forEach(q => {
      if (q.type === 'multiple_choice' && q.options) {
        optionsMap[q.id] = shuffleArray(q.options);
      }
    });
    setShuffledOptionsMap(optionsMap);
  }, [questions]);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const currentQuestion = questions[currentQuestionIndex];
  const isReadingComprehension = currentQuestion.type === 'reading_comprehension';
  const totalQuestions = questions.reduce((acc, q) => {
    if (q.type === 'reading_comprehension') {
      return acc + q.questions.length;
    }
    return acc + 1;
  }, 0);
  
  const answeredCount = Object.keys(answers).length;
  const progress = Math.min(100, (answeredCount / totalQuestions) * 100);
  
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  
  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  
  const handleSubmit = () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    // Small delay to show submitting state
    setTimeout(() => {
      let score = 0;
      questions.forEach(question => {
        if (question.type === 'reading_comprehension') {
          question.questions.forEach(subQ => {
            if (answers[subQ.id] === subQ.answer) {
              score++;
            }
          });
        } else if (question.type === 'writing_prompt') {
          if (answers[question.id] && answers[question.id].trim().split(/\s+/).length >= (question.minWords || 50)) {
            score++;
          }
        } else {
          const userAnswer = answers[question.id];
          const correctAnswer = question.answer;
          
          if (typeof correctAnswer === 'string' && typeof userAnswer === 'string') {
            if (userAnswer.toLowerCase().trim() === correctAnswer.toLowerCase().trim()) {
              score++;
            }
          }
        }
      });
      
      onComplete(score, totalQuestions, answers);
    }, 500);
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const canSubmit = answeredCount === totalQuestions;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Stage {stage.stage}: {stage.cefr} Level</h2>
              <p className="text-gray-600">Question {currentQuestionIndex + 1} of {questions.length}</p>
            </div>
            <div className="text-right">
              <div className={`text-2xl font-bold ${timeLeft < 300 ? 'text-red-600' : 'text-gray-700'}`}>
                {formatTime(timeLeft)}
              </div>
              <p className="text-sm text-gray-500">Time remaining</p>
            </div>
          </div>
          
          <ProgressBar 
            currentStage={stage.stage} 
            totalStages={totalStages} 
            percentage={progress} 
          />
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <QuestionRenderer
            question={currentQuestion}
            answer={
              isReadingComprehension 
                ? answers 
                : answers[currentQuestion.id]
            }
            onAnswer={handleAnswer}
            shuffledOptions={shuffledOptionsMap[currentQuestion.id] || currentQuestion.options}
          />
        </div>
        
        <div className="flex justify-between items-center">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-400 transition-colors"
          >
            ← Previous
          </button>
          
          <div className="text-sm text-gray-600">
            {answeredCount} of {totalQuestions} answered
          </div>
          
          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:from-green-700 hover:to-green-800 transition-all shadow-lg"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Stage'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Next →
            </button>
          )}
        </div>
        
        {!canSubmit && currentQuestionIndex === questions.length - 1 && (
          <div className="mt-4 text-center">
            <p className="text-red-600 font-medium">Please answer all questions before submitting</p>
          </div>
        )}
      </div>
    </div>
  );
}
